import { api } from "./apiSlice";

export type TrailerDeviceStatus = "Online" | "Offline" | "Unplugged" | "Removed";

export interface AnyTrackLocation {
  deviceId: string;
  vehicleName: string;
  licensePlateNumber: string | null;
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  locationTime: string | null;
  reportTime: string | null;
  /** 0 = idle, 1 = driving, 2 = ACC off */
  drivingStatus: number | null;
  batteryLevel: number | null;
  /** External input voltage. > 0 means device is plugged in. */
  externalVoltage: number | null;
  /** 0 = not charging, 1 = charging */
  charging: number | null;
  /** 0 = off, 1 = on */
  acc: number | null;
  /** Device status from backend: Online, Offline, Unplugged, Removed */
  deviceStatus: TrailerDeviceStatus | null;
  /** True when the company intentionally removed the device */
  isRemoved: boolean;
}

export interface DeviceSummary {
  deviceId: string;
  vehicleName: string | null;
  licensePlateNumber: string | null;
  currentStatus: string;
}

export interface DeviceDetail {
  deviceId: string;
  vehicleName: string | null;
  licensePlateNumber: string | null;
  currentStatus: string;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastBatteryLevel: number | null;
  lastExternalVoltage: number | null;
  isRemoved: boolean;
  unplugDetectedAt: string | null;
}

interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export const anyTrackApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAnyTrackLocations: builder.query<AnyTrackLocation[], void>({
      query: () => "anytrack/locations",
      transformResponse: (response: ApiResponse<AnyTrackLocation[]>) =>
        response.data ?? [],
      providesTags: ["AnyTrackLocations"],
    }),
    removeDevice: builder.mutation<void, { deviceId: string; notes?: string }>({
      query: ({ deviceId, notes }) => ({
        url: `anytrack/devices/${deviceId}/remove`,
        method: "POST",
        body: { notes },
      }),
      invalidatesTags: ["AnyTrackLocations"],
    }),
    restoreDevice: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `anytrack/devices/${deviceId}/restore`,
        method: "POST",
      }),
      invalidatesTags: ["AnyTrackLocations"],
    }),
    linkDeviceToTrailer: builder.mutation<void, { deviceId: string; trailerId: number }>({
      query: ({ deviceId, trailerId }) => ({
        url: `anytrack/devices/${deviceId}/link/${trailerId}`,
        method: "POST",
      }),
      invalidatesTags: ["AnyTrackLocations"],
    }),
    unlinkDevice: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `anytrack/devices/${deviceId}/unlink`,
        method: "POST",
      }),
      invalidatesTags: ["AnyTrackLocations"],
    }),
    getAvailableDevices: builder.query<DeviceSummary[], void>({
      query: () => "anytrack/devices/available",
      transformResponse: (response: ApiResponse<DeviceSummary[]>) =>
        response.data ?? [],
    }),
    getDeviceByTrailer: builder.query<DeviceDetail | null, number>({
      query: (trailerId) => `anytrack/devices/by-trailer/${trailerId}`,
      transformResponse: (response: ApiResponse<DeviceDetail | null>) =>
        response.data,
    }),
  }),
});

export const {
  useGetAnyTrackLocationsQuery,
  useRemoveDeviceMutation,
  useRestoreDeviceMutation,
  useLinkDeviceToTrailerMutation,
  useUnlinkDeviceMutation,
  useGetAvailableDevicesQuery,
  useGetDeviceByTrailerQuery,
} = anyTrackApi;
